import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';
import type { Loan } from './types';

const TYPES = [
  { value: 'emprestimo', label: 'Empréstimo' },
  { value: 'financiamento', label: 'Financiamento' },
  { value: 'emprestimo_pessoal', label: 'Empréstimo pessoal' },
  { value: 'emprestimo_empresarial', label: 'Empréstimo empresarial' },
  { value: 'cheque_especial', label: 'Cheque especial' },
  { value: 'rotativo', label: 'Rotativo do cartão' },
  { value: 'parcelamento_fatura', label: 'Parcelamento de fatura' },
  { value: 'divida_fornecedor', label: 'Dívida com fornecedor' },
  { value: 'imposto_parcelado', label: 'Imposto parcelado' },
  { value: 'consorcio', label: 'Consórcio' },
];

/**
 * Cadastro de divida — RF-DIV-01, RF-DIV-02.
 *
 * A TAXA ENTRA EM PERCENTUAL, como esta no contrato. Aceitar decimal tambem
 * obrigaria o sistema a adivinhar se `1.99` significa 1,99% ou 199%, e
 * adivinhar errado em taxa de juros produz um cronograma inteiro sem sentido.
 */
export function LoanForm({
  loan,
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  /** Nulo cadastra; preenchido edita. */
  loan?: Loan | null;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  const isEdit = loan != null;

  const [description, setDescription] = useState(loan?.description ?? '');
  const [type, setType] = useState(loan?.type ?? 'emprestimo');
  const [entityId, setEntityId] = useState(reference.entities[0]?.id ?? 0);
  const [creditorName, setCreditorName] = useState(loan?.creditor_name ?? '');
  const [amount, setAmount] = useState('');
  const [iof, setIof] = useState('');
  const [fees, setFees] = useState('');
  const [insurance, setInsurance] = useState('');
  const [ratePercent, setRatePercent] = useState('');
  const [installments, setInstallments] = useState('');
  const [system, setSystem] = useState('price');
  const [graceMonths, setGraceMonths] = useState('0');
  const [graceType, setGraceType] = useState('');
  const [contractedAt, setContractedAt] = useState(today);
  const [firstDueDate, setFirstDueDate] = useState('');
  const [accountId, setAccountId] = useState('');
  /**
   * Registrar a liberação no caixa.
   *
   * ================================================================
   * NASCE MARCADO, MAS SE DESMARCA SOZINHO EM DÍVIDA ANTIGA
   * ================================================================
   *
   * Vinha sempre marcado, e o texto para empréstimo dizia apenas "deixe
   * desmarcado ao cadastrar uma dívida antiga" — seis palavras que não contam
   * O QUE ACONTECE se ficar marcado.
   *
   * O resultado numa base real: três dívidas antigas cadastradas com a caixa
   * marcada, e R$ 114.890,86 creditados em duas contas que nunca receberam
   * esse dinheiro. Os saldos ficaram R$ 63 mil e R$ 51 mil acima do real, e
   * isso contaminou painel e previsão de caixa.
   *
   * Agora a data decide o padrão: contratada antes de o controle começar, o
   * dinheiro entrou fora daqui, e a caixa nasce desmarcada. O usuário ainda
   * pode marcar — o que muda é para que lado ele erra quando não presta
   * atenção.
   */
  const [registerDisbursement, setRegisterDisbursement] = useState(true);
  const [disbursementTouched, setDisbursementTouched] = useState(false);
  const [isEssential, setIsEssential] = useState(loan?.is_essential ?? false);

  /**
   * CONSÓRCIO É UM FORMULÁRIO DIFERENTE, e não o mesmo com campos vazios.
   *
   * Consórcio não tem juros: o custo é a taxa de administração, já diluída nas
   * parcelas que a administradora informa. Não há IOF, TAC, sistema de
   * amortização a escolher, seguro por parcela nem carência.
   *
   * Mostrar esses campos obrigaria a digitar zero para descrever a realidade —
   * e um zero digitado à mão vira um dado que ninguém sabe se é verdade ou
   * preenchimento. Some tudo: sobra valor contratado, parcelas e datas.
   */
  const isConsortium = type === 'consorcio';

  /**
   * A data de contratação decide o padrão — enquanto o usuário não decidir.
   *
   * `disbursementTouched` protege a escolha dele: uma vez que ele mexeu na
   * caixa, trocar a data não desfaz o que ele marcou. Sugestão que sobrescreve
   * decisão é pior que sugestão nenhuma.
   */
  const controlStart =
    reference.entities.find((e) => e.id === entityId)?.control_start_date ?? null;
  const isOldDebt = controlStart !== null && contractedAt < controlStart;

  useEffect(() => {
    if (!disbursementTouched) {
      setRegisterDisbursement(!isOldDebt);
    }
  }, [isOldDebt, disbursementTouched]);

  /**
   * Por onde o valor é informado.
   *
   * Boa parte das dívidas chega pela PARCELA e não pelo total: a
   * administradora informa "80 de R$ 1.500", o banco oferece "48 de R$ 1.200".
   * Exigir o contratado obriga a calcular à mão o número que a pessoa não tem
   * — e a errar por centavos que depois não fecham com o carnê.
   *
   * A conta fica no servidor. Aqui é só a escolha de qual campo aparece.
   */
  const [valueMode, setValueMode] = useState<'total' | 'parcela'>('total');
  const [installmentAmount, setInstallmentAmount] = useState('');

  const byInstallment = valueMode === 'parcela';

  /**
   * "Sei a parcela" implica "não sei a taxa".
   *
   * Quem tem só o valor da parcela em mãos — a proposta do banco, o carnê da
   * administradora — normalmente não tem taxa, IOF, TAC, sistema de
   * amortização, seguro nem carência. Manter esses campos visíveis nesse modo
   * pede números que a pessoa não tem, e o que ela digita para seguir adiante
   * é chute.
   *
   * Some tudo por padrão. Quem TEM a taxa no contrato pode revelá-la — o
   * cálculo com juros existe e é mais fiel, só não é obrigatório.
   */
  const [knowsRate, setKnowsRate] = useState(false);
  const simplified = byInstallment && !knowsRate;

  const contracted = parseInputToApi(amount || '0');
  const netReleased = (
    Number(contracted) - Number(parseInputToApi(iof || '0')) - Number(parseInputToApi(fees || '0'))
  ).toFixed(2);

  const accounts = reference.accounts.filter((a) => a.entity_id === entityId);
  const hasGrace = Number(graceMonths) > 0;

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        /**
         * A edição manda só o que MUDOU.
         *
         * Reenviar tudo faria o servidor refazer o cronograma a cada
         * salvamento — e refazer é recusado assim que houver parcela paga.
         * Corrigir o nome do credor pararia de funcionar no dia em que a
         * primeira parcela fosse quitada, sem motivo nenhum.
         */
        const changes: Record<string, unknown> = {};

        if (description.trim() !== loan.description) changes.description = description.trim();
        if ((creditorName || null) !== loan.creditor_name) {
          changes.creditor_name = creditorName || null;
        }
        // Marca descritiva: nao toca em valor nem cronograma, entao muda mesmo
        // com parcela paga.
        if (isEssential !== loan.is_essential) changes.is_essential = isEssential;

        return api.patch<{ meta?: { note?: string } }>(
          `/debts/${loan.id}`,
          changes,
          { entityIds: selectedIds },
        );
      }

      return api.post('/debts', {
        description,
        entity_id: entityId,
        type,
        ...(creditorName ? { creditor_name: creditorName } : {}),
        is_essential: isEssential,
        ...(byInstallment
          ? { installment_amount: parseInputToApi(installmentAmount) }
          : { contracted_amount: contracted }),

        /*
          No consórcio, nada disso vai no corpo — nem zerado.

          O servidor normaliza de qualquer forma, mas mandar `iof_amount: "0"`
          gravaria um zero declarado onde o certo é "não se aplica". Meses
          depois, ninguém distingue um dos outros.
        */
        ...(isConsortium
          ? { installments_count: Number(installments) }
          : {
              ...(iof ? { iof_amount: parseInputToApi(iof) } : {}),
              ...(fees ? { fees_amount: parseInputToApi(fees) } : {}),
              ...(insurance ? { insurance_per_installment: parseInputToApi(insurance) } : {}),
              interest_rate_monthly_percent: Number(ratePercent.replace(',', '.')),
              installments_count: Number(installments),
              amortization_system: system,
              grace_months: Number(graceMonths),
              ...(hasGrace ? { grace_type: graceType } : {}),
            }),
        // Pela parcela: a taxa só vai quando o usuário declarou saber qual é.
        // Sem ela o servidor trata como juros zero — a parcela inteira é
        // principal, e é isso que o aviso na tela explica.
        ...(byInstallment && knowsRate
          ? {
              interest_rate_monthly_percent: Number(ratePercent.replace(',', '.')),
              amortization_system: system,
            }
          : {}),
        contracted_at: contractedAt,
        first_due_date: firstDueDate,
        ...(accountId ? { account_id: Number(accountId) } : {}),
        register_disbursement: registerDisbursement && accountId !== '',
      }, { entityIds: selectedIds });
    },
    onSuccess: (res) => {
      const note = (res as { meta?: { note?: string } })?.meta?.note;
      if (note) window.alert(note);
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;

  const canSubmit = isEdit
    ? description.trim().length >= 3
    : description.trim().length >= 3 &&
    (byInstallment
      ? Number(parseInputToApi(installmentAmount || '0')) > 0
      : Number(contracted) > 0) &&
    Number(installments) > 0 &&
    firstDueDate !== '' &&
    // A taxa é exigida em tudo MENOS consórcio, onde o campo nem existe.
    // Sem esta ressalva, o botão ficaria desabilitado para sempre e o
    // usuário não teria nada na tela indicando o que falta preencher.
    (isConsortium || simplified || ratePercent !== '') &&
    (isConsortium || byInstallment || !hasGrace || graceType !== '');

  return (
    <Modal title={isEdit ? 'Editar dívida' : 'Nova dívida'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.values(error.errors).map((m, i) => <li key={i}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Descrição" required>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            minLength={3}
            autoFocus
            placeholder="Ex.: Capital de giro Banco X"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo" required>
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Entidade" required>
            <select
              value={entityId}
              onChange={(e) => { setEntityId(Number(e.target.value)); setAccountId(''); }}
              className="input w-full"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
        </div>

        {/*
          A edição só oferece descrição e credor.

          Valores, taxa e prazo mudam o cronograma inteiro, e o servidor recusa
          isso assim que houver parcela paga. Oferecer os campos aqui produziria
          um formulário que às vezes salva e às vezes não, sem nada na tela
          explicando a diferença — enquanto nada foi pago, excluir e cadastrar
          de novo é o caminho claro.
        */}
        {isEdit && (
          <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
            Valores, taxa e prazo não mudam pela edição: eles refazem o cronograma inteiro, e isso
            deixa de ser possível assim que a primeira parcela é paga. Enquanto nada foi pago,
            exclua e cadastre de novo.
          </p>
        )}

        <Field label="Credor">
          <input
            value={creditorName}
            onChange={(e) => setCreditorName(e.target.value)}
            placeholder="Banco, financeira ou pessoa"
            className="input w-full"
          />
        </Field>

        {/*
          Essencial — RF-PAG-03.

          Fica FORA do bloco `!isEdit`: a marca nao mexe em valor, taxa nem
          cronograma, entao continua editavel depois da primeira parcela paga.
          Quem descobre no meio do financiamento que aquela conta nao pode
          atrasar precisa poder marcar.
        */}
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={isEssential}
            onChange={(e) => setIsEssential(e.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600"
          />
          <span>
            Dívida essencial
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Entra na priorização de "se o caixa apertar, o que não pode deixar de ser pago" em
              Contas a pagar. Não muda valor, prazo nem cronograma.
            </span>
          </span>
        </label>

        {/* Bloco que determina o CET — RN-68 */}
        {!isEdit && (<>
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Valores
          </p>

          {/* A escolha de por onde o valor entra. */}
          <div className="mb-2.5 flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
            {([['total', 'Sei o valor total'], ['parcela', 'Sei o valor da parcela']] as const).map(
              ([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setValueMode(id)}
                  className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    valueMode === id
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-600 hover:text-slate-900 dark:text-slate-400'
                  }`}
                >
                  {label}
                </button>
              ),
            )}
          </div>

          <div className={isConsortium || byInstallment ? '' : 'grid grid-cols-3 gap-3'}>
            <Field
              label={
                byInstallment
                  ? 'Valor da parcela'
                  : isConsortium
                    ? 'Valor contratado (carta de crédito)'
                    : 'Contratado'
              }
              required
              hint={
                byInstallment
                  ? 'o total é calculado a partir dela'
                  : isConsortium
                    ? 'as parcelas saem daqui, divididas por igual'
                    : undefined
              }
            >
              <input
                value={byInstallment ? installmentAmount : amount}
                onChange={(e) =>
                  byInstallment
                    ? setInstallmentAmount(maskMoneyInput(e.target.value))
                    : setAmount(maskMoneyInput(e.target.value))
                }
                inputMode="numeric"
                placeholder="0,00"
                className="input w-full text-right"
              />
            </Field>
            {!isConsortium && !byInstallment && (<>
            <Field label="IOF retido">
              <input
                value={iof}
                onChange={(e) => setIof(maskMoneyInput(e.target.value))}
                inputMode="numeric"
                placeholder="0,00"
                className="input w-full text-right"
              />
            </Field>
            <Field label="TAC e tarifas">
              <input
                value={fees}
                onChange={(e) => setFees(maskMoneyInput(e.target.value))}
                inputMode="numeric"
                placeholder="0,00"
                className="input w-full text-right"
              />
            </Field>
            </>)}
          </div>

          {/*
            Prévia do total, quando o valor entra pela parcela.

            Sem juros a conta é exata. Com juros ela é aproximada porque o
            valor presente de uma série não é multiplicação — e a tela diz
            isso, em vez de exibir um número redondo que não vai bater com o
            que o servidor gravar.
          */}
          {byInstallment && Number(parseInputToApi(installmentAmount || '0')) > 0 &&
            Number(installments) > 0 && (
              <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50">
                {isConsortium || simplified || ratePercent === '' ? (
                  <>
                    Total:{' '}
                    <strong>
                      {formatMoney(
                        (
                          Number(parseInputToApi(installmentAmount)) * Number(installments)
                        ).toFixed(2),
                      )}
                    </strong>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      Sem juros, o total é a parcela vezes o número de parcelas.
                    </span>
                  </>
                ) : (
                  <span className="text-slate-600 dark:text-slate-300">
                    O valor financiado é calculado pelo servidor a partir da parcela e da taxa —
                    parte de cada parcela é juros, e não entra no total contratado. Ele aparece
                    aqui depois de salvar.
                  </span>
                )}
              </p>
            )}

          {!byInstallment && isConsortium && Number(contracted) > 0 && Number(installments) > 0 && (
            <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50">
              {installments} parcelas de aproximadamente{' '}
              <strong>{formatMoney((Number(contracted) / Number(installments)).toFixed(2))}</strong>
              <span className="block text-xs text-slate-500 dark:text-slate-400">
                O servidor distribui os centavos para a soma fechar exata. Reajuste anual da carta
                não é previsto aqui — quando acontecer, edite a dívida.
              </span>
            </p>
          )}

          {!isConsortium && !byInstallment && Number(contracted) > 0 && (
            <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/50">
              Entra na conta: <strong>{formatMoney(netReleased)}</strong>
              {Number(netReleased) < Number(contracted) && (
                <span className="text-slate-500 dark:text-slate-400">
                  {' '}— as parcelas são calculadas sobre {formatMoney(contracted)}, e é essa
                  diferença que faz o CET subir acima da taxa anunciada.
                </span>
              )}
            </p>
          )}
        </div>

        {/* Consórcio ou valor pela parcela: só o número de parcelas. */}
        {(isConsortium || byInstallment) && (
          <>
            <Field label="Parcelas" required hint="quantas faltam ou quantas são no total">
              <input
                value={installments}
                onChange={(e) => setInstallments(e.target.value.replace(/\D/g, '').slice(0, 3))}
                inputMode="numeric"
                placeholder="80"
                className="input w-full text-right"
              />
            </Field>

            {/*
              A taxa é opcional aqui, e a consequência de omiti-la precisa
              estar escrita: sem ela a parcela inteira é principal, então o DRE
              não mostra despesa de juros e não há CET. Para controlar o que se
              deve isso basta; para saber quanto o dinheiro custa, não.
            */}
            {!isConsortium && (
              <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 dark:bg-slate-800/50">
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={knowsRate}
                    onChange={(e) => setKnowsRate(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Sei a taxa de juros do contrato
                    <span className="block text-xs text-slate-500 dark:text-slate-400">
                      {knowsRate
                        ? 'Com a taxa, o sistema separa juros de principal em cada parcela — '
                          + 'os juros viram despesa no DRE e o CET é calculado.'
                        : 'Sem a taxa, a parcela inteira conta como principal: você controla o '
                          + 'que deve e o que já pagou, mas o DRE não mostra despesa de juros e '
                          + 'não há CET.'}
                    </span>
                  </span>
                </label>

                {knowsRate && (
                  <div className="mt-2.5 grid grid-cols-2 gap-3">
                    <Field label="Taxa % a.m." required hint="como no contrato">
                      <input
                        value={ratePercent}
                        onChange={(e) => setRatePercent(e.target.value.replace(/[^\d.,]/g, ''))}
                        inputMode="decimal"
                        placeholder="1,99"
                        className="input w-full text-right"
                      />
                    </Field>
                    <Field label="Sistema" required>
                      <select
                        value={system}
                        onChange={(e) => setSystem(e.target.value)}
                        className="input w-full"
                      >
                        <option value="price">PRICE</option>
                        <option value="sac">SAC</option>
                        <option value="bullet">Bullet</option>
                      </select>
                    </Field>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!isConsortium && !byInstallment && (<>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Taxa % a.m." required hint="como no contrato">
            <input
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value.replace(/[^\d.,]/g, ''))}
              inputMode="decimal"
              placeholder="1,99"
              className="input w-full text-right"
            />
          </Field>
          <Field label="Parcelas" required>
            <input
              value={installments}
              onChange={(e) => setInstallments(e.target.value.replace(/\D/g, '').slice(0, 3))}
              inputMode="numeric"
              placeholder="24"
              className="input w-full text-right"
            />
          </Field>
          <Field label="Sistema" required>
            <select value={system} onChange={(e) => setSystem(e.target.value)} className="input w-full">
              <option value="price">PRICE</option>
              <option value="sac">SAC</option>
              <option value="bullet">Bullet</option>
            </select>
          </Field>
        </div>

        <Field label="Seguro por parcela" hint="entra no CET e é despesa, não amortização">
          <input
            value={insurance}
            onChange={(e) => setInsurance(maskMoneyInput(e.target.value))}
            inputMode="numeric"
            placeholder="0,00"
            className="input w-full text-right"
          />
        </Field>
        </>)}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Contratado em" required>
            <input
              type="date"
              value={contractedAt}
              onChange={(e) => setContractedAt(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
          <Field label="1º vencimento" required>
            <input
              type="date"
              value={firstDueDate}
              onChange={(e) => setFirstDueDate(e.target.value)}
              required
              min={contractedAt}
              className="input w-full"
            />
          </Field>
        </div>

        {/* RN-66 — a escolha que muda muito o custo total. Não existe em
            consórcio, nem quando o valor entra pela parcela. */}
        {!isConsortium && !byInstallment && (<>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Carência (meses)">
            <input
              value={graceMonths}
              onChange={(e) => setGraceMonths(e.target.value.replace(/\D/g, '').slice(0, 3))}
              inputMode="numeric"
              className="input w-full text-right"
            />
          </Field>
          {hasGrace && (
            <Field label="Juros da carência" required>
              <select
                value={graceType}
                onChange={(e) => setGraceType(e.target.value)}
                className="input w-full"
              >
                <option value="">Selecione</option>
                <option value="juros_pagos">Pagos mês a mês</option>
                <option value="juros_capitalizados">Capitalizados na dívida</option>
              </select>
            </Field>
          )}
        </div>

        {hasGrace && graceType === 'juros_capitalizados' && (
          <p className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            Com juros capitalizados, a dívida <strong>cresce</strong> durante a carência: você começa
            a amortizar um saldo maior do que pegou emprestado.
          </p>
        )}
        </>)}

        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <Field
            label={
              isConsortium ? 'Conta onde a carta de crédito entrou' : 'Conta onde o dinheiro entrou'
            }
          >
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="input w-full"
            >
              <option value="">Não informar</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>

          <label className="mt-2 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={registerDisbursement}
              onChange={(e) => {
                setRegisterDisbursement(e.target.checked);
                setDisbursementTouched(true);
              }}
              disabled={accountId === ''}
              className="mt-0.5"
            />
            <span className={accountId === '' ? 'text-slate-400' : ''}>
              {isConsortium ? 'Registrar a entrada da carta de crédito' : 'Registrar a liberação no caixa'}

              {/*
                O MESMO TEXTO PARA TODOS OS TIPOS.

                A explicação completa existia só no consórcio; empréstimo e
                financiamento recebiam "deixe desmarcado ao cadastrar uma
                dívida antiga" — que não diz o que acontece se ficar marcado.

                E foi em empréstimo e financiamento que o erro aconteceu.
              */}
              <span className="block text-xs text-slate-500 dark:text-slate-400">
                O saldo da conta sobe e o passivo nasce. Não vira receita — dinheiro que se deve
                não é lucro.
              </span>

              <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                <strong>Deixe desmarcado se você já recebeu esse dinheiro antes de usar o
                sistema.</strong>{' '}
                Ele entrou e saiu fora daqui — registrá-lo agora faria o saldo da conta subir hoje
                por um dinheiro que já foi gasto. Marque só{' '}
                {isConsortium ? 'nos consórcios contemplados agora' : 'quando o valor cair na conta de verdade'}.
              </span>
            </span>
          </label>

          {/*
            O AVISO COM O NÚMERO, e não uma frase genérica.
            
            "O saldo da conta vai subir" é abstrato. "O saldo da SICRED vai
            subir R$ 21.792,30" é verificável na hora — e é o que faz alguém
            parar e pensar se aquele dinheiro caiu ali de verdade.
            
            Aparece só quando a caixa está marcada e há valor e conta: aviso
            que aparece sempre vira paisagem.
          */}
          {registerDisbursement && accountId !== '' && Number(parseInputToApi(amount || '0')) > 0 && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              O saldo da conta{' '}
              <strong>{accounts.find((a) => String(a.id) === accountId)?.name}</strong> vai subir{' '}
              <strong>{formatMoney(parseInputToApi(amount || '0'))}</strong> na data da
              contratação. Marque só se esse dinheiro caiu nessa conta de verdade.
              {isOldDebt && (
                <span className="mt-1 block font-medium">
                  Esta dívida foi contratada antes de você começar a controlar esta entidade no
                  sistema — normalmente o dinheiro já entrou e já foi usado.
                </span>
              )}
            </p>
          )}
        </div>

        </>)}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending
              ? 'Salvando…'
              : isEdit
                ? 'Salvar alterações'
                : isConsortium
                  ? 'Cadastrar consórcio'
                  : 'Cadastrar dívida'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
